import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Route, BookOpen, CheckCircle2, Clock, ChevronRight, Loader2,
  ChevronDown, Play, Lock, ArrowLeft
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { AdminLayout } from "../../components/layout/AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const statusBadge = {
  completed: "bg-green-500/10 text-green-600 border-green-500/20",
  in_progress: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  assigned: "bg-slate-100 text-slate-600 border-slate-200",
};

const courseStatusStyle = {
  completed: { bg: "bg-green-500/10", text: "text-green-600", label: "Completed", icon: CheckCircle2 },
  in_progress: { bg: "bg-blue-500/10", text: "text-blue-600", label: "In Progress", icon: Play },
  enrolled: { bg: "bg-slate-100", text: "text-slate-600", label: "Not Started", icon: BookOpen },
  not_enrolled: { bg: "bg-slate-50", text: "text-slate-400", label: "Locked", icon: Lock },
};

const LearnerLearningPathsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPath, setExpandedPath] = useState(null);
  const [pathDetail, setPathDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/my/learning-paths");
        setPaths(res.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const togglePathDetail = async (pathId) => {
    if (expandedPath === pathId) {
      setExpandedPath(null);
      setPathDetail(null);
      return;
    }
    setExpandedPath(pathId);
    setLoadingDetail(true);
    try {
      const res = await api.get(`/my/learning-paths/${pathId}`);
      setPathDetail(res.data);
    } catch (e) {
      console.error(e);
      setPathDetail(null);
    } finally { setLoadingDetail(false); }
  };

  return (
    <AdminLayout title="Learning Paths" variant={variant}>
      <div className="space-y-6" data-testid="learner-learning-paths">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
            My Learning Paths
          </h1>
          <p className="text-muted-foreground text-sm">Track your assigned learning paths and course progress</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : paths.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Route className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-muted-foreground">No learning paths assigned yet</p>
              <p className="text-xs text-muted-foreground mt-1">Your admin will assign learning paths to guide your training</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {paths.map((p, i) => {
              const isExpanded = expandedPath === p.path?.id;
              return (
                <motion.div key={p.assignment_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card className={`transition-colors ${isExpanded ? "border-primary/30 shadow-sm" : "hover:border-primary/20"}`} data-testid={`learning-path-${i}`}>
                    <CardContent className="p-5">
                      {/* Path Header */}
                      <div
                        className="flex items-start gap-4 cursor-pointer"
                        onClick={() => togglePathDetail(p.path?.id)}
                        data-testid={`toggle-path-${i}`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <Route className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-base">{p.path?.title}</h3>
                            <Badge className={statusBadge[p.status] || statusBadge.assigned}>
                              {p.status === "completed" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                              {p.status === "in_progress" && <Clock className="w-3 h-3 mr-1" />}
                              {p.status?.replace("_", " ")}
                            </Badge>
                          </div>
                          {p.path?.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{p.path.description}</p>
                          )}
                          <div className="flex items-center gap-4 mb-2">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <BookOpen className="w-3 h-3" /> {p.completed_courses}/{p.total_courses} courses
                            </span>
                            {p.path?.estimated_hours > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {p.path.estimated_hours}h estimated
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <Progress value={p.progress_percent} className="h-2 flex-1" />
                            <span className="text-sm font-bold text-primary">{p.progress_percent}%</span>
                          </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform shrink-0 mt-1 ${isExpanded ? "rotate-180" : ""}`} />
                      </div>

                      {/* Expanded Course List */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 pt-4 border-t space-y-2" data-testid={`path-courses-${i}`}>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                                Course Sequence
                              </p>
                              {loadingDetail ? (
                                <div className="flex items-center justify-center py-6">
                                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                                </div>
                              ) : pathDetail?.courses?.length > 0 ? (
                                pathDetail.courses.map((course, ci) => {
                                  const style = courseStatusStyle[course.status] || courseStatusStyle.not_enrolled;
                                  const StatusIcon = style.icon;
                                  return (
                                    <div
                                      key={course.course_id}
                                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                        course.status !== "not_enrolled" ? "hover:bg-muted/30 cursor-pointer" : "opacity-60"
                                      }`}
                                      onClick={() => {
                                        if (course.status !== "not_enrolled") navigate(`/course/${course.course_id}`);
                                      }}
                                      data-testid={`path-course-${ci}`}
                                    >
                                      {/* Step number */}
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                        course.status === "completed" ? "bg-green-500 text-white" :
                                        course.status === "in_progress" ? "bg-primary text-primary-foreground" :
                                        "bg-muted text-muted-foreground"
                                      }`}>
                                        {course.status === "completed" ? <CheckCircle2 className="w-4 h-4" /> : course.order}
                                      </div>

                                      {/* Course info */}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <h4 className="font-medium text-sm truncate">{course.title}</h4>
                                          {course.is_required && (
                                            <Badge className="text-[9px] bg-red-500/10 text-red-600 border-red-500/20">Required</Badge>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
                                          <span>{course.course_type?.includes("scorm") ? "eLearning" : course.course_type === "ilt" ? "ILT" : course.course_type}</span>
                                          {course.duration_hours > 0 && <span>{course.duration_hours}h</span>}
                                          {course.category && <span>{course.category}</span>}
                                        </div>
                                      </div>

                                      {/* Progress */}
                                      <div className="flex items-center gap-2 shrink-0">
                                        {course.status === "in_progress" && (
                                          <div className="w-16">
                                            <Progress value={course.progress} className="h-1.5" />
                                          </div>
                                        )}
                                        <Badge className={`${style.bg} ${style.text} text-[10px]`}>
                                          <StatusIcon className="w-3 h-3 mr-1" />
                                          {style.label}
                                        </Badge>
                                      </div>

                                      {course.status !== "not_enrolled" && (
                                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="text-sm text-muted-foreground text-center py-4">No courses configured</p>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default LearnerLearningPathsPage;
